import NeptuneElement from '../../NeptuneElement.js';

const BREADCRUMB_LABEL_MAX_LENGTH = 15;

/**
 * Neptune Breadcrumb web component that renders a navigable breadcrumb trail from a history array.
 * Supports label truncation with tooltip, an overflow menu for collapsed desktop items,
 * and a compact mobile view showing the first item, an overflow button, and the current page.
 * @extends NeptuneElement
 */
class Breadcrumb extends NeptuneElement {

    templateMarkup = `
        <nav aria-label="Breadcrumb">
            <ul>
                <slot></slot>
            </ul>
        </nav>
    `;

    componentCssUrl = '/src/components/navigation/breadcrumb/breadcrumb.css';

    #breadcrumbHistory;
    #breadcrumbListElement;
    #overflowButtonElement;
    #overflowMenuElement;
    #overflowMenuAttributeObserver;

    constructor() {
        super();

        this.#breadcrumbHistory = [];
        this.#breadcrumbListElement = null;
        this.#overflowButtonElement = null;
        this.#overflowMenuElement = null;
        this.#overflowMenuAttributeObserver = null;

        this.handleBreadcrumbClick = this.handleBreadcrumbClick.bind(this);
    }

    /**
     * Initialises the component after it is connected to the DOM.
     * Delegates to the parent lifecycle then processes initial observed attributes.
     * @returns {Promise<void>}
     */
    async connectedCallback() {
        await super.connectedCallback();
        this.processInitialAttributesAfterRender(Breadcrumb.observedAttributes);
    }

    /**
     * Cleans up the component when it is removed from the DOM.
     * Disconnects the overflow menu observer before delegating to the parent lifecycle.
     * @returns {Promise<void>}
     */
    async disconnectedCallback() {
        this.#disconnectOverflowMenuObserver();
        await super.disconnectedCallback();
    }

    /**
     * Returns the list of attributes the component observes for changes.
     * @returns {string[]} The names of the observed attributes.
     */
    static get observedAttributes() {
        return ['history'];
    }

    /**
     * Responds to observed attribute changes on the host element.
     * Defers processing until the component has fully rendered.
     * @param {string} name The name of the changed attribute.
     * @param {string|null} oldValue The previous attribute value.
     * @param {string|null} newValue The new attribute value.
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    /**
     * Processes a single attribute change and triggers a re-render when the attribute is recognised.
     * @param {string} name The name of the attribute that changed.
     * @param {string|null} oldValue The previous attribute value.
     * @param {string|null} newValue The new attribute value.
     */
    processAttribute(name, oldValue, newValue) {
        if ('history' !== name) {
            return;
        }

        this.#breadcrumbHistory = this.#parseHistoryAttribute(newValue);
        this.#renderBreadcrumb();
    }

    /**
     * Attaches the delegated click listener to the shadow root and caches the list element.
     * Called automatically by the parent after the component has rendered.
     * @returns {Promise<void>}
     */
    async setEventListeners() {
        if (!this.isRendered) {
            return;
        }

        this.#breadcrumbListElement = this.shadowRoot?.querySelector('ul') || null;
        this.shadowRoot?.addEventListener('click', this.handleBreadcrumbClick);
    }

    /**
     * Removes the delegated click listener from the shadow root and releases all cached DOM references.
     * @returns {Promise<void>}
     */
    async removeEventListeners() {
        this.shadowRoot?.removeEventListener('click', this.handleBreadcrumbClick);
        this.#disconnectOverflowMenuObserver();

        this.#breadcrumbListElement = null;
        this.#overflowButtonElement = null;
        this.#overflowMenuElement = null;
    }

    /**
     * Clears and rebuilds the full breadcrumb list in the shadow DOM.
     * Renders mobile items first, then desktop items including the first item,
     * optional overflow, and tail items. Starts the overflow menu observer on completion.
     */
    #renderBreadcrumb() {
        if (!this.isRendered) {
            return;
        }

        const breadcrumbListElement = this.#breadcrumbListElement || this.shadowRoot?.querySelector('ul');
        if (!breadcrumbListElement) {
            return;
        }

        this.#breadcrumbListElement = breadcrumbListElement;
        this.#disconnectOverflowMenuObserver();

        breadcrumbListElement.innerHTML = '';

        if (!Array.isArray(this.#breadcrumbHistory) || 0 === this.#breadcrumbHistory.length) {
            const slot = document.createElement('slot');
            breadcrumbListElement.appendChild(slot);
            return;
        }

        const mobileListItems = this.#createMobileListItems(this.#breadcrumbHistory);
        mobileListItems.forEach(item => breadcrumbListElement.appendChild(item));

        const renderModel = this.#buildRenderModel(this.#breadcrumbHistory);

        breadcrumbListElement.appendChild(this.#createDesktopListItem(renderModel.first, false, true));

        if (renderModel.shouldCollapse) {
            breadcrumbListElement.appendChild(
                this.#createOverflowListItem(
                    renderModel.hidden,
                    'overflow',
                    '--neptune-breadcrumb-overflow-anchor'
                )
            );
        }

        renderModel.tail.forEach((item, index) => {
            const isCurrentPage = index === renderModel.tail.length - 1;
            breadcrumbListElement.appendChild(this.#createDesktopListItem(item, isCurrentPage));
        });

        this.#observeAllOverflowMenus();
    }

    /**
     * Handles click events delegated from the shadow root.
     * Routes the event to one of three handlers: overflow trigger toggle,
     * overflow menu link selection, or standard breadcrumb link selection.
     * @param {MouseEvent} event The click event originating from the shadow root.
     */
    handleBreadcrumbClick(event) {
        const overflowButtonElement = this.#findElementInComposedPath(event, (element) => {
            return element?.getAttribute?.('data-role') === 'overflow-trigger';
        });
        if (overflowButtonElement) {
            const overflowMenuElement = overflowButtonElement.nextElementSibling;
            if (overflowMenuElement && 'NEPTUNE-MENU' === overflowMenuElement.tagName) {
                overflowMenuElement.toggleAttribute('open');
                overflowButtonElement.setAttribute(
                    'aria-expanded',
                    overflowMenuElement.hasAttribute('open') ? 'true' : 'false'
                );
            }
            return;
        }

        const overflowMenuLinkElement = event.target?.closest?.('neptune-menu[data-role="overflow-menu"] a');
        if (overflowMenuLinkElement) {
            event.preventDefault?.();
            const overflowMenuElement = overflowMenuLinkElement.closest('neptune-menu');
            if (overflowMenuElement) {
                const overflowTriggerElement = overflowMenuElement.previousElementSibling;
                overflowMenuElement.removeAttribute('open');
                if (overflowTriggerElement) {
                    overflowTriggerElement.setAttribute('aria-expanded', 'false');
                }
            }
            this.#dispatchItemSelectedEvent(overflowMenuLinkElement);
            return;
        }

        const breadcrumbLinkElement = this.#findElementInComposedPath(event, (element) => {
            return element?.tagName === 'A' && !element?.closest?.('neptune-menu');
        });
        if (breadcrumbLinkElement) {
            event.preventDefault?.();
            this.#dispatchItemSelectedEvent(breadcrumbLinkElement);
        }
    }

    /**
     * Walks the composed event path and returns the first element matching the predicate.
     * @param {Event} event The DOM event whose composed path is searched.
     * @param {Function} predicate A function that receives each element and returns a boolean.
     * @returns {Element|null} The first matching element, or null if none is found.
     */
    #findElementInComposedPath(event, predicate) {
        const path = event?.composedPath?.() || [];
        for (let index = 0; index < path.length; index++) {
            const element = path[index];
            if (predicate(element)) {
                return element;
            }
        }
        return null;
    }

    /**
     * Dispatches a `neptune-breadcrumb-item-selected` custom event from the host element.
     * Reads the item name from aria-label or text content and the route from href.
     * @param {HTMLAnchorElement} linkElement The anchor element that was activated.
     */
    #dispatchItemSelectedEvent(linkElement) {
        const name = linkElement.getAttribute('aria-label') || linkElement.textContent?.trim() || '';
        const route = linkElement.getAttribute('href') || '';
        this.dispatchEvent(new CustomEvent('neptune-breadcrumb-item-selected', {
            bubbles: true,
            composed: true,
            detail: { name, route }
        }));
    }

    /**
     * Parses the raw `history` attribute string into a validated array of breadcrumb items.
     * Returns an empty array if the value is missing, not valid JSON, or not an array.
     * @param {string|null} value The raw JSON string from the history attribute.
     * @returns {Array<{name: string, route: string}>} The parsed and sanitised breadcrumb items.
     */
    #parseHistoryAttribute(value) {
        if (!value) {
            return [];
        }

        try {
            const parsed = JSON.parse(value);
            if (!Array.isArray(parsed)) {
                return [];
            }

            return parsed
                .filter((item) => item && typeof item === 'object')
                .map((item) => ({
                    name: String(item.name ?? '').trim(),
                    route: String(item.route ?? '').trim()
                }))
                .filter((item) => item.name.length > 0);
        } catch (error) {
            console.error('Invalid history JSON:', error);
            return [];
        }
    }

    /**
     * Derives the desktop render model from the full history array.
     * Collapses the list into first, hidden, and tail buckets when there are more than four entries.
     * @param {Array<{name: string, route: string}>} history The full breadcrumb history.
     * @returns {{shouldCollapse: boolean, first: object, tail: object[], hidden: object[]}} The render model.
     */
    #buildRenderModel(history) {
        const totalItemCount = history.length;
        const shouldCollapse = totalItemCount > 4;

        if (!shouldCollapse) {
            return {
                shouldCollapse: false,
                first: history[0],
                tail: history.slice(1),
                hidden: []
            };
        }

        return {
            shouldCollapse: true,
            first: history[0],
            tail: history.slice(totalItemCount - 2),
            hidden: history.slice(1, totalItemCount - 2)
        };
    }

    /**
     * Builds the ordered array of mobile-only `li` elements: first item link,
     * optional overflow button, and current page span.
     * Returns an empty array when the history contains fewer than two items.
     * @param {Array<{name: string, route: string}>} history The full breadcrumb history.
     * @returns {HTMLLIElement[]} The ordered array of mobile list item elements.
     */
    #createMobileListItems(history) {
        if (!Array.isArray(history) || history.length < 2) {
            return [];
        }

        const items = [];

        const firstListItem = document.createElement('li');
        firstListItem.setAttribute('data-role', 'mobile-first');
        firstListItem.appendChild(this.#createLinkNode(history[0]));
        items.push(firstListItem);

        if (history.length > 2) {
            items.push(this.#createOverflowListItem(history.slice(1, history.length - 1),'mobile-dots','--neptune-breadcrumb-mobile-overflow-anchor'));
        }

        const lastListItem = document.createElement('li');
        lastListItem.setAttribute('data-role', 'mobile-last');
        lastListItem.appendChild(this.#createCurrentPageSpanNode(history[history.length - 1]));
        items.push(lastListItem);

        return items;
    }

    /**
     * Builds a single desktop `li` element for a breadcrumb item.
     * The first item renders without truncation, the current page renders as a non-link span,
     * and all other items render as truncatable links with tooltip support.
     * @param {{name: string, route: string}} item The breadcrumb item to render.
     * @param {boolean} isCurrentPage Whether this item represents the current page.
     * @param {boolean} [isFirstItem=false] Whether this is the first item in the trail.
     * @returns {HTMLLIElement} The constructed list item element.
     */
    #createDesktopListItem(item, isCurrentPage, isFirstItem = false) {
        const listItemElement = document.createElement('li');

        if (isCurrentPage) {
            listItemElement.appendChild(this.#createCurrentPageSpanNode(item));
            return listItemElement;
        }

        if (isFirstItem) {
            const linkElement = document.createElement('a');
            linkElement.classList.add('font-size-lg', 'font-weight-regular');
            linkElement.href = item.route || '#';
            linkElement.textContent = item.name;
            listItemElement.appendChild(linkElement);
            return listItemElement;
        }

        listItemElement.appendChild(this.#createLinkNode(item));
        return listItemElement;
    }

    /**
     * Builds an `li` element containing a neptune-button overflow trigger and a neptune-menu.
     * Used for both the desktop overflow (collapsed items) and the mobile dots button.
     * @param {Array<{name: string, route: string}>} hiddenItems The items to list inside the overflow menu.
     * @param {string} listItemRole The data-role value applied to the wrapping `li`.
     * @param {string} anchorName The CSS anchor name used to position the overflow menu relative to its trigger.
     * @returns {HTMLLIElement} The constructed overflow list item element.
     */
    #createOverflowListItem(hiddenItems, listItemRole, anchorName) {
        const listItemElement = document.createElement('li');
        listItemElement.setAttribute('data-role', listItemRole);

        const overflowButtonElement = document.createElement('neptune-button');
        overflowButtonElement.setAttribute('type', 'tertiary');
        overflowButtonElement.setAttribute('size', 'small');
        overflowButtonElement.className = 'overflow-trigger';
        overflowButtonElement.textContent = '. . .';
        overflowButtonElement.setAttribute('data-role', 'overflow-trigger');
        overflowButtonElement.setAttribute('aria-label', 'Show breadcrumb history');
        overflowButtonElement.setAttribute('aria-haspopup', 'menu');
        overflowButtonElement.setAttribute('aria-expanded', 'false');
        overflowButtonElement.style.setProperty('anchor-name', anchorName);

        const overflowMenuElement = document.createElement('neptune-menu');
        overflowMenuElement.setAttribute('data-role', 'overflow-menu');
        overflowMenuElement.style.setProperty('position-anchor', anchorName);

        this.#populateOverflowMenuWithItems(overflowMenuElement, hiddenItems);

        listItemElement.appendChild(overflowButtonElement);
        listItemElement.appendChild(overflowMenuElement);
        return listItemElement;
    }

    /**
     * Appends a neptune-menu-item for each breadcrumb item into the given neptune-menu element.
     * Each item renders as a link node with truncation and tooltip support.
     * @param {HTMLElement} menuElement The neptune-menu element to populate.
     * @param {Array<{name: string, route: string}>} items The breadcrumb items to add as menu entries.
     */
    #populateOverflowMenuWithItems(menuElement, items) {
        (Array.isArray(items) ? items : []).forEach((crumb) => {
            const menuItemElement = document.createElement('neptune-menu-item');
            menuItemElement.appendChild(this.#createLinkNode(crumb));
            menuElement.appendChild(menuItemElement);
        });
    }

    /**
     * Creates an anchor element for a breadcrumb item with truncation and tooltip support.
     * Returns a plain anchor when the label fits, or a tooltip-wrapped anchor when truncated.
     * @param {{name: string, route: string}} item The breadcrumb item to render.
     * @returns {HTMLAnchorElement|HTMLElement} The anchor element, or a tooltip-wrapped anchor.
     */
    #createLinkNode(item) {
        const linkElement = document.createElement('a');
        linkElement.classList.add('font-size-lg', 'font-weight-regular');
        linkElement.href = item.route || '#';
        const { isTruncated, fullText } = this.#applyTruncatedLabel(linkElement, item.name);
        if (isTruncated) {
            return this.#wrapWithTooltip(linkElement, fullText);
        }
        return linkElement;
    }

    /**
     * Creates a span element marked as the current page for a breadcrumb item.
     * Returns a plain span when the label fits, or a tooltip-wrapped span when truncated.
     * @param {{name: string, route: string}} item The breadcrumb item to render.
     * @returns {HTMLSpanElement|HTMLElement} The span element, or a tooltip-wrapped span.
     */
    #createCurrentPageSpanNode(item) {
        const spanElement = document.createElement('span');
        spanElement.classList.add('font-size-lg', 'font-weight-regular');
        spanElement.setAttribute('aria-current', 'page');
        const { isTruncated, fullText } = this.#applyTruncatedLabel(spanElement, item.name);
        if (isTruncated) {
            return this.#wrapWithTooltip(spanElement, fullText);
        }
        return spanElement;
    }

    /**
     * Wraps a trigger element inside a neptune-tooltip with the full text as tooltip content.
     * Sets the trigger slot to `trigger-element` and adds a `tooltip-text` slot span.
     * @param {HTMLElement} triggerElement The element that triggers the tooltip on hover or focus.
     * @param {string} fullText The untruncated label text shown inside the tooltip.
     * @returns {HTMLElement} The neptune-tooltip element containing the trigger and tooltip text.
     */
    #wrapWithTooltip(triggerElement, fullText) {
        const tooltipElement = document.createElement('neptune-tooltip');
        triggerElement.setAttribute('slot', 'trigger-element');
        const tooltipTextElement = document.createElement('span');
        tooltipTextElement.setAttribute('slot', 'tooltip-text');
        tooltipTextElement.textContent = fullText;
        tooltipElement.appendChild(triggerElement);
        tooltipElement.appendChild(tooltipTextElement);
        return tooltipElement;
    }

    /**
     * Computes display and full text for a label, applying truncation when needed.
     * Truncates at BREADCRUMB_LABEL_MAX_LENGTH characters and appends an ellipsis.
     * @param {string} name The raw label text.
     * @returns {{fullText: string, displayText: string, isTruncated: boolean}} The label data.
     */
    #truncateLabel(name) {
        const label = String(name ?? '');
        const isTruncated = label.length > BREADCRUMB_LABEL_MAX_LENGTH;

        return {
            fullText: label,
            displayText: isTruncated
                ? `${label.slice(0, BREADCRUMB_LABEL_MAX_LENGTH).trimEnd()}...`
                : label,
            isTruncated
        };
    }

    /**
     * Sets the text content of an element to the truncated label and updates aria-label when truncated.
     * @param {HTMLElement} element The element whose text content is updated.
     * @param {string} name The raw label text to truncate and apply.
     * @returns {{isTruncated: boolean, fullText: string}} Whether truncation occurred and the full original text.
     */
    #applyTruncatedLabel(element, name) {
        const { fullText, displayText, isTruncated } = this.#truncateLabel(name);

        element.textContent = displayText;
        if (isTruncated) {
            element.setAttribute('aria-label', fullText);
        } else {
            element.removeAttribute('aria-label');
        }

        return { isTruncated, fullText };
    }

    /**
     * Caches overflow DOM references and attaches a MutationObserver to every overflow menu.
     * The observer keeps aria-expanded on each trigger in sync when its menu open state changes.
     */
    #observeAllOverflowMenus() {
        this.#overflowButtonElement = this.shadowRoot?.querySelector('[data-role="overflow-trigger"]') || null;
        this.#overflowMenuElement = this.shadowRoot?.querySelector('neptune-menu[data-role="overflow-menu"]') || null;

        const allOverflowMenus = [...(this.shadowRoot?.querySelectorAll('neptune-menu[data-role="overflow-menu"]') || [])];

        if (allOverflowMenus.length === 0) {
            return;
        }

        this.#overflowMenuAttributeObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const menu = mutation.target;
                const trigger = menu.previousElementSibling;
                if (trigger) {
                    trigger.setAttribute(
                        'aria-expanded',
                        menu.hasAttribute('open') ? 'true' : 'false'
                    );
                }
            });
        });

        allOverflowMenus.forEach((menu) => {
            this.#overflowMenuAttributeObserver.observe(menu, {
                attributes: true,
                attributeFilter: ['open']
            });
        });
    }

    /**
     * Disconnects and clears the overflow menu MutationObserver if one is currently active.
     */
    #disconnectOverflowMenuObserver() {
        if (!this.#overflowMenuAttributeObserver) {
            return;
        }

        this.#overflowMenuAttributeObserver.disconnect();
        this.#overflowMenuAttributeObserver = null;
    }
}

customElements.define('neptune-breadcrumb', Breadcrumb);

export default Breadcrumb;